import type {
  AuthUser,
  AvailableSlot,
  AvailabilityNextResponse,
  Booking,
  BookingCancellationInput,
  BookingUpdateInput,
  BookingUpdateResult,
  BookingExtraSelection,
  BookingParticipant,
  BookingParticipantInput,
  CatalogPosition,
  CatalogPositionsInput,
  BookingRequest,
  BookingCheckout,
  CouponRedemption,
  CartCheckout,
  CartItem,
  CustomerInput,
  CalendarResponse,
  CustomerSummary,
  Customer360,
  CustomerActivity,
  CustomerActivityInput,
  CustomerSegment,
  CustomerSegmentInput,
  DashboardOverview,
  DashboardOverviewQuery,
  DashboardSummary,
  MobileBootstrap,
  ReportBookingPoint,
  ReportOverview,
  ReportRevenuePoint,
  EventSummary,
  EntityMedia,
  EntityMediaType,
  EventPeriod,
  ProviderEventAssignment,
  ProviderCheckInResult,
  PrivacyRequestSummary,
  AcceptUserInvitationResult,
  UserInvitationSummary,
  EventCheckout,
  EventCheckoutRequest,
  EventQuoteRequest,
  ServiceQuoteRequest,
  HealthResponse,
  IntegrationAccountBinding,
  IntegrationCatalogEntry,
  IntegrationSummary,
  InvoiceSummary,
  BookingDeposit,
  PaymentLink,
  NotificationChannel,
  NotificationCatalog,
  NotificationDefinition,
  NotificationDefinitionInput,
  NotificationDefinitionUpdate,
  NotificationPreferences,
  NotificationTemplate,
  NotificationTemplateUpdate,
  NotificationSummary,
  PaymentSummary,
  PaymentCheckout,
  PaymentLinkStatusResponse,
  PaymentProviderEnvironment,
  PaymentProviderName,
  PaymentTransactionSummary,
  PriceQuote,
  PricingRule,
  PricingRuleInput,
  PricingRulePatch,
  RefundSummary,
  RecurringFrequency,
  RecurringRule,
  RecurringSeriesCheckout,
  RecurringSeriesCheckoutInput,
  RecurringSeriesPreview,
  RecurringSeriesPreviewInput,
  RecurringSeries,
  ServiceRecurrenceSettings,
  ServicePaymentMode,
  ShoppingCart,
  SignedAction,
  ServiceSummary,
  TaxDefinition,
  TaxInput,
  TaxPatch,
  CustomFieldDefinition,
  CustomFieldInput,
  CustomFieldPatch,
  CustomFieldValue,
  CustomFieldValueInput,
  ProviderDetail,
  ProviderAvailabilityRequest,
  ProviderPortalNotification,
  ProviderInput,
  ProviderSummary,
  ProviderException,
  ProviderServiceAssignment,
  WeeklySchedule,
  SlotHoldRequest,
  SlotHoldResult,
  StaffBookingRequest,
  ApiKeyCreated,
  ApiKeySummary,
  ApiKeyScope,
  BackupRun,
  CredentialVersion,
  DeviceSummary,
  MfaCredential,
  MfaEnrollment,
  MfaMethod,
  WebAuthnPublicKeyOptions,
  SecurityEvent,
  SsoConnection,
  ScimUser,
  ScimGroup,
  SupportAccessGrant,
  SupportAccessSession,
  WebhookEndpoint,
  PasswordChangeResult,
  AdminPasswordRecoveryResult,
  PublicBranding,
  PublicProviderListing,
  PublicProviderProfile,
  PublicProviderProfileDraft,
  ProviderPasswordRecoveryResetRequest,
  ProviderPasswordRecoveryRequestResult,
  ProviderPasswordRecoveryResult,
  ProviderPortalMetrics,
  ConciergeConfigPatch,
  ConciergeConfigResponse,
  Organization,
  OrganizationContext,
  OrganizationContextResponse,
  OrganizationCreateInput,
  OrganizationCreationResponse,
  OrganizationListItem,
  OrganizationMembership,
  OrganizationOnboardingPatchInput,
  OrganizationOnboardingRun,
  OrganizationProvisioningCreateInput,
  OrganizationProvisioningCreateResponse,
  OrganizationProvisioningPatchInput,
  OrganizationProvisioningSummary
} from "@kuroo/contracts";
import type {
  AdminExport,
  AdminExportRequest,
  AdminImport,
  AvailabilityException,
  AvailabilityRule,
  CatalogCategory,
  CatalogExtra,
  CatalogLocation,
  CatalogPackage,
  CatalogProvider,
  CatalogResource,
  Coupon,
  CouponDetails,
  CouponTarget,
  EventAttendee,
  EventTicket,
  LocationInput,
  LocationPatch,
  WaitlistEntry
} from "./client-catalog-types";
import type {
  KurooClientOptions,
  LoginResult,
  MfaLoginChallenge,
  RecurringBookingOccurrence,
  RecurringSeriesInput,
  ScimGroupInput,
  ScimListResponse,
  ScimPatchOperation,
  ScimServiceProviderConfig,
  ScimTokenCreated,
  ScimTokenSummary,
  ScimUserInput,
  SessionSummary,
  TeamUser,
  WebhookDeliverySummary
} from "./client-auth-types";
import * as sdkAvailability from "./availability";
import type { SdkAvailabilityContext } from "./availability";
import * as sdkAdmin from "./admin";
import type { SdkAdminContext } from "./admin";
import * as sdkCart from "./cart";
import type { SdkCartContext } from "./cart";
import * as sdkPrivacy from "./privacy";
import type { SdkPrivacyContext } from "./privacy";
import * as sdkAuth from "./auth";
import type { SdkAuthContext } from "./auth";
import * as sdkAnalytics from "./analytics";
import type { SdkAnalyticsContext } from "./analytics";
import * as sdkBooking from "./booking";
import type { SdkBookingContext } from "./booking";
import * as sdkCoupons from "./coupons";
import type { SdkCouponsContext } from "./coupons";
import * as sdkCustomer from "./customer";
import type { SdkCustomerContext } from "./customer";
import * as sdkEvents from "./events";
import type { SdkEventsContext } from "./events";
import * as sdkIntegrations from "./integrations";
import type { SdkIntegrationsContext } from "./integrations";
import * as sdkIdentity from "./identity";
import type { SdkIdentityContext } from "./identity";
import { createAdminImport, createEntityMedia, deleteEntityMedia, listEntityMedia, type SdkMediaContext, updateEntityMedia, uploadFile, uploadMyProviderPhoto, uploadProviderPhoto } from "./media";
import * as sdkNotifications from "./notifications";
import type { SdkNotificationsContext } from "./notifications";
import * as sdkCatalog from "./catalog";
import type { SdkCatalogContext } from "./catalog";
import * as sdkOrganization from "./organization";
import type { SdkOrganizationContext } from "./organization";
import * as sdkOperations from "./operations";
import type { SdkOperationsContext } from "./operations";
import * as sdkPayment from "./payment";
import type { SdkPaymentContext } from "./payment";
import { createSdkTransport } from "./transport";

export type { AcceptUserInvitationResult, AdminPasswordRecoveryResult, ApiKeyCreated, ApiKeySummary, ApiKeyScope, AvailabilityNextResponse, BackupRun, CatalogPosition, CatalogPositionsInput, ConciergeConfigPatch, ConciergeConfigResponse, CredentialVersion, CustomerInput, CustomFieldDefinition, CustomFieldInput, CustomFieldPatch, CustomFieldValue, CustomFieldValueInput, DeviceSummary, EntityMedia, EntityMediaType, IntegrationAccountBinding, MfaCredential, MfaEnrollment, MfaMethod, NotificationCatalog, NotificationChannel, NotificationDefinition, NotificationDefinitionInput, NotificationDefinitionUpdate, NotificationPreferences, NotificationTemplateUpdate, Organization, OrganizationContext, OrganizationContextResponse, OrganizationCreateInput, OrganizationCreationResponse, OrganizationListItem, OrganizationMembership, OrganizationOnboardingPatchInput, OrganizationOnboardingRun, OrganizationProvisioningCreateInput, OrganizationProvisioningCreateResponse, OrganizationProvisioningPatchInput, OrganizationProvisioningSummary, PasswordChangeResult, PublicBranding, PublicProviderListing, PublicProviderProfile, PublicProviderProfileDraft, ProviderAvailabilityRequest, ProviderDetail, ProviderException, ProviderInput, ProviderPasswordRecoveryRequest, ProviderPasswordRecoveryResetRequest, ProviderPasswordRecoveryRequestResult, ProviderPasswordRecoveryResult, ProviderPortalMetrics, ProviderPortalNotification, ProviderServiceAssignment, ProviderSummary, SecurityEvent, SsoConnection, ScimUser, ScimGroup, SupportAccessGrant, SupportAccessSession, TaxDefinition, TaxInput, TaxPatch, WebhookEndpoint, WebAuthnPublicKeyOptions, WeeklySchedule } from "@kuroo/contracts";
export type { BookingAdjustment, BookingCancellationInput, BookingExtraSelection, BookingCheckout, BookingUpdateInput, BookingUpdateResult, StaffBookingRequest, EventCheckout, EventCheckoutRequest, EventQuoteRequest, ServiceQuoteRequest, PriceQuote, PriceQuoteBreakdown, CouponRedemption, PricingRule, PricingRuleInput, PricingRulePatch } from "@kuroo/contracts";

export type { paths as KurooApiPaths } from "./generated-api";
export type {
  AdminExport,
  AdminExportRequest,
  AdminImport,
  AvailabilityException,
  AvailabilityRule,
  CatalogCategory,
  CatalogExtra,
  CatalogLocation,
  CatalogPackage,
  CatalogProvider,
  CatalogResource,
  Coupon,
  CouponDetails,
  CouponTarget,
  EventAttendee,
  EventTicket,
  LocationInput,
  LocationPatch,
  WaitlistEntry
} from "./client-catalog-types";
export type {
  KurooClientOptions,
  LoginResult,
  MfaLoginChallenge,
  RecurringBookingOccurrence,
  RecurringSeriesInput,
  ScimGroupInput,
  ScimListResponse,
  ScimPatchOperation,
  ScimServiceProviderConfig,
  ScimTokenCreated,
  ScimTokenSummary,
  ScimUserInput,
  SessionSummary,
  TeamUser,
  WebhookDeliverySummary
} from "./client-auth-types";
export { KurooApiError } from "./errors";

export class KurooClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly credentials: RequestCredentials;
  private readonly transport: ReturnType<typeof createSdkTransport>;
  private selectedOrganizationId?: string;

  constructor(options: KurooClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    // Browser implementations of `fetch` require the global object as their
    // receiver. Keep injected test/client implementations untouched, but bind
    // the native function so a client method call cannot fail with
    // "Illegal invocation" after it is stored on the instance.
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.credentials = options.credentials ?? "include";
    this.selectedOrganizationId = options.organizationId?.trim() || undefined;
    this.transport = createSdkTransport({
      baseUrl: this.baseUrl,
      fetchImpl: this.fetchImpl,
      credentials: this.credentials,
      provisioningLocalToken: options.provisioningLocalToken?.trim() || undefined,
      organizationId: () => this.selectedOrganizationId
    });
  }

  /**
   * Select the tenant used by subsequent authenticated API calls. The API
   * verifies this selector against the session/API-key membership; the SDK
   * never treats it as an authorization grant.
   */
  setOrganizationId(organizationId?: string): void {
    this.selectedOrganizationId = organizationId?.trim() || undefined;
  }

  getOrganizationId(): string | undefined {
    return this.selectedOrganizationId;
  }

  private organizationHeaders(input?: HeadersInit): Headers {
    const headers = new Headers(input ?? {});
    if (this.selectedOrganizationId) headers.set("X-Kuroo-Organization-Id", this.selectedOrganizationId);
    return headers;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    return this.transport.request<T>(path, init);
  }

  private mediaContext(): SdkMediaContext {
    return {
      baseUrl: this.baseUrl,
      fetchImpl: this.fetchImpl,
      credentials: this.credentials,
      organizationHeaders: (input) => this.organizationHeaders(input),
      request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init)
    };
  }

  private authContext(): SdkAuthContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private organizationApiContext(): SdkOrganizationContext {
    return {
      request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init),
      setOrganizationId: (organizationId) => this.setOrganizationId(organizationId),
      getOrganizationId: () => this.selectedOrganizationId
    };
  }

  private catalogContext(): SdkCatalogContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private availabilityContext(): SdkAvailabilityContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private adminContext(): SdkAdminContext {
    return { baseUrl: this.baseUrl, request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private cartContext(): SdkCartContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private privacyContext(): SdkPrivacyContext {
    return { baseUrl: this.baseUrl, request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private couponsContext(): SdkCouponsContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private eventsContext(): SdkEventsContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private bookingContext(): SdkBookingContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private analyticsContext(): SdkAnalyticsContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private customerContext(): SdkCustomerContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private paymentContext(): SdkPaymentContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private notificationsContext(): SdkNotificationsContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private integrationsContext(): SdkIntegrationsContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  private identityContext(): SdkIdentityContext {
    return {
      baseUrl: this.baseUrl,
      request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init),
      requestScim: <T>(path: string, token: string, init?: RequestInit) => this.requestScim<T>(path, token, init)
    };
  }

  private operationsContext(): SdkOperationsContext {
    return { request: <T>(path: string, init?: RequestInit) => this.request<T>(path, init) };
  }

  /**
   * SCIM is deliberately kept separate from the Kuroo REST envelope: RFC
   * 7644 requires a bare resource/ListResponse and `application/scim+json`.
   * This helper still maps failures to the SDK's typed error so callers do not
   * need a second error-handling model.
   */
  private async requestScim<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
    return this.transport.requestScim<T>(path, token, init);
  }

  health(): Promise<HealthResponse> {
    return this.request<HealthResponse>("/api/v1/health");
  }

  currentUser(): Promise<AuthUser> {
    return this.request<AuthUser>("/api/v1/auth/me");
  }

  organizations(): Promise<OrganizationListItem[]> {
    return sdkOrganization.organizations(this.organizationApiContext());
  }

  organization(organizationId: string): Promise<OrganizationListItem> {
    return sdkOrganization.organization(this.organizationApiContext(), organizationId);
  }

  organizationContext(): Promise<OrganizationContextResponse> {
    return sdkOrganization.organizationContext(this.organizationApiContext());
  }

  switchOrganization(organizationId: string): Promise<OrganizationContextResponse> {
    return sdkOrganization.switchOrganization(this.organizationApiContext(), organizationId);
  }

  createOrganization(input: OrganizationCreateInput): Promise<OrganizationCreationResponse> {
    return sdkOrganization.createOrganization(this.organizationApiContext(), input);
  }

  provisioningOrganizations(): Promise<{ organizations: OrganizationProvisioningSummary[]; operator: { email: string; source: string } }> {
    return sdkOrganization.provisioningOrganizations(this.organizationApiContext());
  }

  createProvisioningOrganization(input: OrganizationProvisioningCreateInput): Promise<OrganizationProvisioningCreateResponse> {
    return sdkOrganization.createProvisioningOrganization(this.organizationApiContext(), input);
  }

  updateProvisioningOrganization(organizationId: string, input: OrganizationProvisioningPatchInput): Promise<{ organization: OrganizationProvisioningSummary; readiness: Record<string, boolean> | null }> {
    return sdkOrganization.updateProvisioningOrganization(this.organizationApiContext(), organizationId, input);
  }

  organizationOnboarding(organizationId: string | undefined = this.selectedOrganizationId, runId?: string): Promise<OrganizationOnboardingRun> {
    return sdkOrganization.organizationOnboarding(this.organizationApiContext(), organizationId, runId);
  }

  updateOrganizationOnboarding(input: OrganizationOnboardingPatchInput, organizationId: string | undefined = this.selectedOrganizationId, runId?: string): Promise<OrganizationOnboardingRun> {
    return sdkOrganization.updateOrganizationOnboarding(this.organizationApiContext(), input, organizationId, runId);
  }

  publicConfig(options: { organizationSlug?: string; signal?: AbortSignal } = {}): Promise<PublicBranding> {
    return sdkOrganization.publicConfig(this.organizationApiContext(), options);
  }

  publicProviders(options: { serviceId?: string; locationId?: string; search?: string; organizationSlug?: string; signal?: AbortSignal } = {}): Promise<PublicProviderListing[]> {
    return sdkOrganization.publicProviders(this.organizationApiContext(), options);
  }

  publicProvider(slug: string, options: { organizationSlug?: string; signal?: AbortSignal } = {}): Promise<PublicProviderProfile> {
    return sdkOrganization.publicProvider(this.organizationApiContext(), slug, options);
  }

  publicProfileDraft(providerId?: string): Promise<PublicProviderProfileDraft> {
    return sdkOrganization.publicProfileDraft(this.organizationApiContext(), providerId);
  }

  updatePublicProfileDraft(input: Partial<Omit<PublicProviderProfileDraft, "providerId" | "status" | "pendingEditorial" | "publishedAt" | "updatedAt">> & { providerId?: string }): Promise<PublicProviderProfileDraft> {
    return sdkOrganization.updatePublicProfileDraft(this.organizationApiContext(), input);
  }

  publishPublicProfile(providerId?: string): Promise<PublicProviderProfileDraft> {
    return sdkOrganization.publishPublicProfile(this.organizationApiContext(), providerId);
  }

  unpublishPublicProfile(providerId?: string): Promise<PublicProviderProfileDraft> {
    return sdkOrganization.unpublishPublicProfile(this.organizationApiContext(), providerId);
  }

  login(email: string, password: string): Promise<LoginResult> {
    return sdkAuth.login(this.authContext(), email, password);
  }

  changePassword(input: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<PasswordChangeResult> {
    return sdkAuth.changePassword(this.authContext(), input);
  }

  requestProviderPasswordRecovery(email: string): Promise<ProviderPasswordRecoveryRequestResult> {
    return sdkAuth.requestProviderPasswordRecovery(this.authContext(), email);
  }

  completeProviderPasswordRecovery(input: ProviderPasswordRecoveryResetRequest): Promise<ProviderPasswordRecoveryResult> {
    return sdkAuth.completeProviderPasswordRecovery(this.authContext(), input);
  }

  recoverAdminPassword(input: { email: string; newPassword: string; confirmPassword: string; newEmail?: string }, recoveryToken: string): Promise<AdminPasswordRecoveryResult> {
    return sdkAuth.recoverAdminPassword(this.authContext(), input, recoveryToken);
  }

  mfaCredentials(): Promise<MfaCredential[]> {
    return sdkAuth.mfaCredentials(this.authContext());
  }

  enrollMfa(input: { method: MfaMethod; label?: string }): Promise<MfaEnrollment & { challengeId: string; expiresAt: string; recoveryCodes?: string[] }> {
    return sdkAuth.enrollMfa(this.authContext(), input);
  }

  verifyMfa(input: { challengeId: string; challenge: string; code?: string; assertion?: Record<string, unknown> }): Promise<AuthUser> {
    return sdkAuth.verifyMfa(this.authContext(), input);
  }

  recoverMfa(input: { challengeId: string; code: string }): Promise<AuthUser> {
    return sdkAuth.recoverMfa(this.authContext(), input);
  }

  disableMfa(password: string): Promise<{ disabled: boolean }> {
    return sdkAuth.disableMfa(this.authContext(), password);
  }

  sessions(): Promise<SessionSummary[]> {
    return sdkAuth.sessions(this.authContext());
  }

  revokeSession(sessionId: string): Promise<{ id: string; revoked: boolean; current: boolean }> {
    return sdkAuth.revokeSession(this.authContext(), sessionId);
  }

  revokeOtherSessions(): Promise<{ revoked: number; current: boolean }> {
    return sdkAuth.revokeOtherSessions(this.authContext());
  }

  devices(): Promise<DeviceSummary[]> {
    return sdkAuth.devices(this.authContext());
  }

  revokeDevice(deviceId: string): Promise<{ id: string; revoked: boolean; current: boolean }> {
    return sdkAuth.revokeDevice(this.authContext(), deviceId);
  }

  logout(): Promise<{ loggedOut: boolean }> {
    return sdkAuth.logout(this.authContext());
  }

  users(): Promise<TeamUser[]> {
    return sdkAdmin.users(this.adminContext());
  }

  createUser(input: { email: string; firstName: string; lastName?: string; role?: AuthUser["role"]; password?: string }): Promise<TeamUser> {
    return sdkAdmin.createUser(this.adminContext(), input);
  }

  providersAdmin(filters: { search?: string; status?: "active" | "inactive" } = {}): Promise<ProviderSummary[]> {
    return sdkAdmin.providersAdmin(this.adminContext(), filters);
  }

  locationsAdmin(filters: { status?: CatalogLocation["status"] } = {}): Promise<CatalogLocation[]> {
    return sdkAdmin.locationsAdmin(this.adminContext(), filters);
  }

  createProvider(input: ProviderInput): Promise<ProviderDetail> {
    return sdkAdmin.createProvider(this.adminContext(), input);
  }

  provider(providerId: string): Promise<ProviderDetail> {
    return sdkAdmin.provider(this.adminContext(), providerId);
  }

  updateProvider(providerId: string, input: Partial<ProviderInput>): Promise<ProviderDetail> {
    return sdkAdmin.updateProvider(this.adminContext(), providerId, input);
  }

  uploadProviderPhoto(providerId: string, file: File): Promise<ProviderDetail> {
    return uploadProviderPhoto(this.mediaContext(), providerId, file);
  }

  updateUser(userId: string, input: { firstName?: string; lastName?: string; role?: AuthUser["role"]; status?: AuthUser["status"] }): Promise<Partial<TeamUser> & { id: string }> {
    return sdkAdmin.updateUser(this.adminContext(), userId, input);
  }

  services(options: { organizationSlug?: string; signal?: AbortSignal } = {}): Promise<ServiceSummary[]> {
    return sdkCatalog.services(this.catalogContext(), options);
  }

  createService(input: { name: string; durationMinutes: number; priceCents: number; currency?: string; capacity?: number; priceUnit?: ServiceSummary["priceUnit"]; status?: ServiceSummary["status"]; categoryId?: string; paymentMode?: ServicePaymentMode; googleMeetEnabled?: boolean; bufferBeforeMinutes?: number; bufferAfterMinutes?: number; slotIntervalMinutes?: number | null; minNoticeMinutes?: number; maxAdvanceDays?: number | null }): Promise<ServiceSummary> {
    return sdkCatalog.createService(this.catalogContext(), input);
  }

  service(serviceId: string): Promise<ServiceSummary> {
    return sdkCatalog.service(this.catalogContext(), serviceId);
  }

  updateService(serviceId: string, input: { name?: string; slug?: string; description?: string | null; durationMinutes?: number; priceCents?: number; currency?: string; capacity?: number; priceUnit?: ServiceSummary["priceUnit"]; status?: ServiceSummary["status"]; categoryId?: string | null; paymentMode?: ServicePaymentMode; googleMeetEnabled?: boolean; bufferBeforeMinutes?: number; bufferAfterMinutes?: number; slotIntervalMinutes?: number | null; minNoticeMinutes?: number; maxAdvanceDays?: number | null; recurrence?: Partial<ServiceRecurrenceSettings> }): Promise<ServiceSummary> {
    return sdkCatalog.updateService(this.catalogContext(), serviceId, input);
  }

  deleteService(serviceId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
    return sdkCatalog.deleteService(this.catalogContext(), serviceId);
  }

  pricingRules(filters: { targetKind?: PricingRule["targetKind"]; targetId?: string; enabled?: boolean } = {}): Promise<PricingRule[]> {
    return sdkCatalog.pricingRules(this.catalogContext(), filters);
  }

  createPricingRule(input: PricingRuleInput): Promise<PricingRule> {
    return sdkCatalog.createPricingRule(this.catalogContext(), input);
  }

  updatePricingRule(ruleId: string, input: PricingRulePatch): Promise<PricingRule> {
    return sdkCatalog.updatePricingRule(this.catalogContext(), ruleId, input);
  }

  deletePricingRule(ruleId: string): Promise<{ id: string; deleted: boolean }> {
    return sdkCatalog.deletePricingRule(this.catalogContext(), ruleId);
  }

  quoteService(input: ServiceQuoteRequest, options: { signal?: AbortSignal } = {}): Promise<PriceQuote> {
    return sdkCatalog.quoteService(this.catalogContext(), input, options);
  }

  /** Calculates and serializes a final service proposal in one operation. */
  quoteAndHoldService(input: SlotHoldRequest, options: { signal?: AbortSignal } = {}): Promise<SlotHoldResult> {
    return sdkCatalog.quoteAndHoldService(this.catalogContext(), input, options);
  }

  /** Generic quote facade retained for integrations that pass a forward-compatible payload. */
  quote(input: Record<string, unknown> & { idempotencyKey?: string }): Promise<PriceQuote> {
    return sdkCatalog.quote(this.catalogContext(), input);
  }

  categories(options: { includeArchived?: boolean; status?: CatalogCategory["status"] } = {}): Promise<CatalogCategory[]> {
    return sdkCatalog.categories(this.catalogContext(), options);
  }

  createCategory(input: { name: string; slug?: string; description?: string; sortOrder?: number; status?: CatalogCategory["status"] }): Promise<Pick<CatalogCategory, "id" | "name" | "slug">> {
    return sdkCatalog.createCategory(this.catalogContext(), input);
  }

  category(categoryId: string): Promise<CatalogCategory> {
    return sdkCatalog.category(this.catalogContext(), categoryId);
  }

  updateCategory(categoryId: string, input: { name?: string; slug?: string; description?: string | null; status?: CatalogCategory["status"]; sortOrder?: number }): Promise<CatalogCategory> {
    return sdkCatalog.updateCategory(this.catalogContext(), categoryId, input);
  }

  deleteCategory(categoryId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
    return sdkCatalog.deleteCategory(this.catalogContext(), categoryId);
  }

  taxes(options: { status?: TaxDefinition["status"]; signal?: AbortSignal } = {}): Promise<TaxDefinition[]> {
    return sdkCatalog.taxes(this.catalogContext(), options);
  }

  createTax(input: TaxInput): Promise<TaxDefinition> {
    return sdkCatalog.createTax(this.catalogContext(), input);
  }

  tax(taxId: string): Promise<TaxDefinition> {
    return sdkCatalog.tax(this.catalogContext(), taxId);
  }

  updateTax(taxId: string, input: TaxPatch): Promise<TaxDefinition> {
    return sdkCatalog.updateTax(this.catalogContext(), taxId, input);
  }

  deleteTax(taxId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return sdkCatalog.deleteTax(this.catalogContext(), taxId);
  }

  updateTaxStatus(taxId: string, status: TaxDefinition["status"]): Promise<{ id: string; status: TaxDefinition["status"] }> {
    return sdkCatalog.updateTaxStatus(this.catalogContext(), taxId, status);
  }

  customFields(options: { entityType?: CustomFieldDefinition["entityType"]; status?: CustomFieldDefinition["status"]; includeInactive?: boolean; signal?: AbortSignal } = {}): Promise<CustomFieldDefinition[]> {
    return sdkCatalog.customFields(this.catalogContext(), options);
  }

  createCustomField(input: CustomFieldInput): Promise<CustomFieldDefinition> {
    return sdkCatalog.createCustomField(this.catalogContext(), input);
  }

  customField(fieldId: string): Promise<CustomFieldDefinition> {
    return sdkCatalog.customField(this.catalogContext(), fieldId);
  }

  updateCustomField(fieldId: string, input: CustomFieldPatch): Promise<CustomFieldDefinition> {
    return sdkCatalog.updateCustomField(this.catalogContext(), fieldId, input);
  }

  deleteCustomField(fieldId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return sdkCatalog.deleteCustomField(this.catalogContext(), fieldId);
  }

  customFieldValue(fieldId: string, entityId: string): Promise<CustomFieldValue> {
    return sdkCatalog.customFieldValue(this.catalogContext(), fieldId, entityId);
  }

  setCustomFieldValue(fieldId: string, entityId: string, input: CustomFieldValueInput): Promise<CustomFieldValue> {
    return sdkCatalog.setCustomFieldValue(this.catalogContext(), fieldId, entityId, input);
  }

  updateCustomFieldValue(fieldId: string, entityId: string, input: CustomFieldValueInput): Promise<CustomFieldValue> {
    return sdkCatalog.updateCustomFieldValue(this.catalogContext(), fieldId, entityId, input);
  }

  deleteCustomFieldValue(fieldId: string, entityId: string): Promise<{ fieldId: string; entityId: string; deleted: boolean }> {
    return sdkCatalog.deleteCustomFieldValue(this.catalogContext(), fieldId, entityId);
  }

  settings(): Promise<Array<{ key: string; value: unknown; encrypted: boolean; updatedAt: string }>> {
    return sdkCatalog.settings(this.catalogContext());
  }

  updateSettings(settings: Record<string, unknown>): Promise<Record<string, unknown>> {
    return sdkCatalog.updateSettings(this.catalogContext(), settings);
  }

  conciergeConfig(): Promise<ConciergeConfigResponse> {
    return sdkCatalog.conciergeConfig(this.catalogContext());
  }

  updateConciergeConfig(config: ConciergeConfigPatch): Promise<ConciergeConfigResponse> {
    return sdkCatalog.updateConciergeConfig(this.catalogContext(), config);
  }

  slots(serviceId: string, date: string, options: { providerId?: string; locationId?: string; extras?: BookingExtraSelection[]; persons?: number; includePricing?: boolean; signal?: AbortSignal } = {}): Promise<AvailableSlot[]> {
    return sdkBooking.slots(this.bookingContext(), serviceId, date, options);
  }

  nextAvailability(serviceId: string, options: { fromDate?: string; days?: number; providerId?: string; locationId?: string; signal?: AbortSignal } = {}): Promise<AvailabilityNextResponse> {
    return sdkBooking.nextAvailability(this.bookingContext(), serviceId, options);
  }

  holdSlot(input: SlotHoldRequest): Promise<SlotHoldResult> {
    return sdkBooking.holdSlot(this.bookingContext(), input);
  }

  releaseHold(holdId: string, slotKey: string, quoteId?: string, holdCapability?: string): Promise<{ released: boolean }> {
    return sdkBooking.releaseHold(this.bookingContext(), holdId, slotKey, quoteId, holdCapability);
  }

  createBooking(input: BookingRequest): Promise<Booking | BookingCheckout> {
    return sdkBooking.createBooking(this.bookingContext(), input);
  }

  createAdminBooking(input: StaffBookingRequest): Promise<Booking | BookingCheckout> {
    return sdkBooking.createAdminBooking(this.bookingContext(), input);
  }

  recurringSeries(): Promise<RecurringSeries[]> {
    return sdkBooking.recurringSeries(this.bookingContext());
  }

  recurringSeriesDetail(seriesId: string): Promise<RecurringSeries> {
    return sdkBooking.recurringSeriesDetail(this.bookingContext(), seriesId);
  }

  previewRecurringSeries(input: RecurringSeriesPreviewInput, options: { signal?: AbortSignal } = {}): Promise<RecurringSeriesPreview> {
    return sdkBooking.previewRecurringSeries(this.bookingContext(), input, options);
  }

  /** Calculates and serializes every included occurrence of a recurring proposal. */
  previewRecurringSeriesAndHold(input: RecurringSeriesPreviewInput & { idempotencyKey: string }, options: { signal?: AbortSignal } = {}): Promise<RecurringSeriesPreview> {
    return sdkBooking.previewRecurringSeriesAndHold(this.bookingContext(), input, options);
  }

  checkoutRecurringSeries(input: RecurringSeriesCheckoutInput): Promise<RecurringSeriesCheckout> {
    return sdkBooking.checkoutRecurringSeries(this.bookingContext(), input);
  }

  createRecurringSeries(input: RecurringSeriesInput): Promise<RecurringSeries> {
    return sdkBooking.createRecurringSeries(this.bookingContext(), input);
  }

  cancelRecurringSeries(seriesId: string, input: { scope?: "occurrence" | "future" | "all"; occurrenceIds?: string[]; reason?: string } = {}): Promise<{ id: string; status: string; refundId?: string | null; refundPending?: boolean }> {
    return sdkBooking.cancelRecurringSeries(this.bookingContext(), seriesId, input);
  }

  rescheduleRecurringOccurrence(seriesId: string, occurrenceId: string, input: { slotKey: string; startAt: string; endAt: string; providerId?: string | null; locationId?: string | null; idempotencyKey: string }): Promise<RecurringSeries> {
    return sdkBooking.rescheduleRecurringOccurrence(this.bookingContext(), seriesId, occurrenceId, input);
  }

  createRecurringBookings(seriesId: string, occurrences: RecurringBookingOccurrence[], options: { persons?: number; notes?: string; idempotencyKey?: string } = {}): Promise<{ seriesId: string; bookings: Booking[] }> {
    return sdkBooking.createRecurringBookings(this.bookingContext(), seriesId, occurrences, options);
  }

  bookingParticipants(bookingId: string): Promise<BookingParticipant[]> {
    return sdkBooking.bookingParticipants(this.bookingContext(), bookingId);
  }

  addBookingParticipant(bookingId: string, input: BookingParticipantInput): Promise<BookingParticipant> {
    return sdkBooking.addBookingParticipant(this.bookingContext(), bookingId, input);
  }

  removeBookingParticipant(bookingId: string, participantId: string): Promise<{ id: string; deleted: boolean }> {
    return sdkBooking.removeBookingParticipant(this.bookingContext(), bookingId, participantId);
  }

  reassignBooking(bookingId: string, input: { slotKey: string; startAt: string; endAt: string; providerId?: string; locationId?: string; idempotencyKey?: string }): Promise<Booking> {
    return sdkBooking.reassignBooking(this.bookingContext(), bookingId, input);
  }

  createBookingAction(bookingId: string, action: "cancel" = "cancel", ttlSeconds?: number): Promise<SignedAction> {
    return sdkBooking.createBookingAction(this.bookingContext(), bookingId, action, ttlSeconds);
  }

  executeSignedAction(token: string): Promise<{ action: string; id: string; status: string }> {
    return sdkBooking.executeSignedAction(this.bookingContext(), token);
  }

  bookings(): Promise<Booking[]> {
    return sdkBooking.bookings(this.bookingContext());
  }

  booking(bookingId: string): Promise<Booking> {
    return sdkBooking.booking(this.bookingContext(), bookingId);
  }

  updateBooking(bookingId: string, input: BookingUpdateInput): Promise<BookingUpdateResult> {
    return sdkBooking.updateBooking(this.bookingContext(), bookingId, input);
  }

  dashboardSummary(range: { from?: string; to?: string } = {}): Promise<DashboardSummary> {
    return sdkAnalytics.dashboardSummary(this.analyticsContext(), range);
  }

  dashboardOverview(filters: DashboardOverviewQuery = {}): Promise<DashboardOverview> {
    return sdkAnalytics.dashboardOverview(this.analyticsContext(), filters);
  }

  reportOverview(range: { from?: string; to?: string } = {}): Promise<ReportOverview> {
    return sdkAnalytics.reportOverview(this.analyticsContext(), range);
  }

  reportRevenue(range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; points: ReportRevenuePoint[] }> {
    return sdkAnalytics.reportRevenue(this.analyticsContext(), range);
  }

  reportBookings(range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; points: ReportBookingPoint[] }> {
    return sdkAnalytics.reportBookings(this.analyticsContext(), range);
  }

  providerPortalMetrics(range: { from?: string; to?: string } = {}): Promise<ProviderPortalMetrics> {
    return sdkAnalytics.providerPortalMetrics(this.analyticsContext(), range);
  }

  mobileBootstrap(): Promise<MobileBootstrap> {
    return sdkAnalytics.mobileBootstrap(this.analyticsContext());
  }

  mobileAppointments(range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; appointments: Booking[] }> {
    return sdkAnalytics.mobileAppointments(this.analyticsContext(), range);
  }

  mobileEvents(range: { from?: string; to?: string } = {}): Promise<EventSummary[]> {
    return sdkAnalytics.mobileEvents(this.analyticsContext(), range);
  }

  calendar(filters: { from?: string; to?: string; providerId?: string; locationId?: string; serviceId?: string; status?: string; includeEvents?: boolean; includeSlots?: boolean } = {}): Promise<CalendarResponse> {
    return sdkAnalytics.calendar(this.analyticsContext(), filters);
  }

  providerProfile(): Promise<ProviderDetail> {
    return this.request<ProviderDetail>("/api/v1/providers/me");
  }

  updateProviderProfile(input: { firstName?: string; lastName?: string; phone?: string | null; timezone?: string; locale?: string; displayName?: string }): Promise<ProviderDetail> {
    return this.request<ProviderDetail>("/api/v1/providers/me", { method: "PATCH", body: JSON.stringify(input) });
  }

  uploadMyProviderPhoto(file: File): Promise<ProviderDetail> {
    return uploadMyProviderPhoto(this.mediaContext(), file);
  }

  customers(search?: string): Promise<CustomerSummary[]> {
    return sdkCustomer.customers(this.customerContext(), search);
  }

  customer(customerId: string): Promise<Customer360 & { bookings: Booking[] }> {
    return sdkCustomer.customer(this.customerContext(), customerId);
  }

  updateCustomer(customerId: string, input: { firstName?: string; lastName?: string; phone?: string; timezone?: string; locale?: string }): Promise<CustomerSummary & { bookings: Booking[] }> {
    return sdkCustomer.updateCustomer(this.customerContext(), customerId, input);
  }

  updateCustomerProfile(customerId: string, input: { note?: string | null; tags?: string[] }): Promise<Customer360 & { bookings: Booking[] }> {
    return sdkCustomer.updateCustomerProfile(this.customerContext(), customerId, input);
  }

  customerActivities(customerId: string): Promise<CustomerActivity[]> {
    return sdkCustomer.customerActivities(this.customerContext(), customerId);
  }

  createCustomerActivity(customerId: string, input: CustomerActivityInput): Promise<CustomerActivity> {
    return sdkCustomer.createCustomerActivity(this.customerContext(), customerId, input);
  }

  updateCustomerActivity(customerId: string, activityId: string, input: { status: "open" | "completed" }): Promise<CustomerActivity[]> {
    return sdkCustomer.updateCustomerActivity(this.customerContext(), customerId, activityId, input);
  }

  customerSegments(): Promise<CustomerSegment[]> {
    return sdkCustomer.customerSegments(this.customerContext());
  }

  createCustomerSegment(input: CustomerSegmentInput): Promise<CustomerSegment> {
    return sdkCustomer.createCustomerSegment(this.customerContext(), input);
  }

  customerNotificationPreferences(customerId: string): Promise<NotificationPreferences> {
    return sdkCustomer.customerNotificationPreferences(this.customerContext(), customerId);
  }

  updateCustomerNotificationPreferences(customerId: string, input: Partial<Omit<NotificationPreferences, "userId" | "updatedAt">>): Promise<NotificationPreferences> {
    return sdkCustomer.updateCustomerNotificationPreferences(this.customerContext(), customerId, input);
  }

  payments(filters: { from?: string; to?: string; status?: string } = {}): Promise<PaymentSummary[]> {
    return sdkPayment.payments(this.paymentContext(), filters);
  }

  createPaymentCheckout(input: { provider?: PaymentProviderName; integrationAccountId?: string; sourceId?: string; bookingId?: string; invoiceId?: string; recurringSeriesId?: string; paymentLinkToken?: string; environment?: PaymentProviderEnvironment; returnUrls?: { success?: string; pending?: string; failure?: string }; idempotencyKey: string }): Promise<PaymentCheckout> {
    return sdkPayment.createPaymentCheckout(this.paymentContext(), input);
  }

  payment(paymentId: string): Promise<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }> {
    return sdkPayment.payment(this.paymentContext(), paymentId);
  }

  updatePayment(paymentId: string, input: { status?: "pending" | "authorized" | "paid" | "partially_refunded" | "refunded" | "failed" | "cancelled"; statusDetail?: string | null }): Promise<{ id: string; status: string; statusDetail: string | null }> {
    return sdkPayment.updatePayment(this.paymentContext(), paymentId, input);
  }

  deletePayment(paymentId: string): Promise<{ id: string; status: "cancelled"; deleted: boolean }> {
    return sdkPayment.deletePayment(this.paymentContext(), paymentId);
  }

  calculatePaymentAmount(input: { subtotalCents?: number; amountCents?: number; currency?: string; couponCode?: string }): Promise<{ subtotalCents: number; discountCents: number; totalCents: number; currency: string }> {
    return sdkPayment.calculatePaymentAmount(this.paymentContext(), input);
  }

  paymentTransaction(transactionId: string): Promise<{ id: string; paymentId: string; kind: string; amountCents: number; providerTransactionId?: string | null; status: string; currency: string; createdAt: string }> {
    return sdkPayment.paymentTransaction(this.paymentContext(), transactionId);
  }

  refundPaymentById(paymentId: string, input: { amountCents: number; idempotencyKey: string; reason?: string; method?: "mercadopago" | "cash"; occurrenceIds?: string[]; overrideReason?: string }): Promise<RefundSummary> {
    return sdkPayment.refundPaymentById(this.paymentContext(), paymentId, input);
  }

  approveRefund(refundId: string, input: { method: "mercadopago" | "cash"; amountCents?: number; overrideReason?: string; idempotencyKey: string }): Promise<RefundSummary> {
    return sdkPayment.approveRefund(this.paymentContext(), refundId, input);
  }

  rejectRefund(refundId: string, reason: string): Promise<RefundSummary> {
    return sdkPayment.rejectRefund(this.paymentContext(), refundId, reason);
  }

  completeCashRefund(refundId: string, cashReceiptReference: string, idempotencyKey = `cash-delivery:${refundId}:${cashReceiptReference}`): Promise<RefundSummary> {
    return sdkPayment.completeCashRefund(this.paymentContext(), refundId, cashReceiptReference, idempotencyKey);
  }

  retryRefund(refundId: string, idempotencyKey: string): Promise<RefundSummary> {
    return sdkPayment.retryRefund(this.paymentContext(), refundId, idempotencyKey);
  }

  refunds(filters: { status?: string } = {}): Promise<RefundSummary[]> {
    return sdkPayment.refunds(this.paymentContext(), filters);
  }

  capturePayment(paymentId: string): Promise<{ paymentId: string; provider: string; providerPaymentId: string; status: string; statusDetail?: string | null; amountCents: number; currency: string }> {
    return sdkPayment.capturePayment(this.paymentContext(), paymentId);
  }

  paymentTransactions(paymentId: string): Promise<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }> {
    return sdkPayment.paymentTransactions(this.paymentContext(), paymentId);
  }

  invoices(): Promise<InvoiceSummary[]> {
    return sdkPayment.invoices(this.paymentContext());
  }

  invoice(invoiceId: string): Promise<InvoiceSummary> {
    return sdkPayment.invoice(this.paymentContext(), invoiceId);
  }

  createInvoice(input: { bookingId: string; taxRateBps?: number; number?: string; dueAt?: string }): Promise<InvoiceSummary> {
    return sdkPayment.createInvoice(this.paymentContext(), input);
  }

  voidInvoice(invoiceId: string): Promise<{ id: string; status: "void" }> {
    return sdkPayment.voidInvoice(this.paymentContext(), invoiceId);
  }

  bookingDeposit(bookingId: string): Promise<BookingDeposit | null> {
    return sdkPayment.bookingDeposit(this.paymentContext(), bookingId);
  }

  createBookingDeposit(input: { bookingId: string; amountCents: number; dueAt?: string }): Promise<BookingDeposit> {
    return sdkPayment.createBookingDeposit(this.paymentContext(), input);
  }

  createPaymentLink(input: { bookingId?: string; invoiceId?: string; amountCents?: number; ttlSeconds?: number }): Promise<PaymentLink> {
    return sdkPayment.createPaymentLink(this.paymentContext(), input);
  }

  paymentLink(token: string): Promise<PaymentLink> {
    return sdkPayment.paymentLink(this.paymentContext(), token);
  }

  paymentLinkStatus(token: string): Promise<PaymentLinkStatusResponse> {
    return sdkPayment.paymentLinkStatus(this.paymentContext(), token);
  }

  cancelPaymentLink(linkId: string): Promise<{ id: string; status: "cancelled" }> {
    return sdkPayment.cancelPaymentLink(this.paymentContext(), linkId);
  }

  notifications(options: { status?: string; channel?: NotificationChannel; definitionId?: string; recipient?: string } = {}): Promise<NotificationSummary[]> {
    return sdkNotifications.notifications(this.notificationsContext(), options);
  }

  notificationCatalog(): Promise<NotificationCatalog> {
    return sdkNotifications.notificationCatalog(this.notificationsContext());
  }

  createNotificationDefinition(input: NotificationDefinitionInput): Promise<NotificationDefinition> {
    return sdkNotifications.createNotificationDefinition(this.notificationsContext(), input);
  }

  updateNotificationDefinition(definitionId: string, input: NotificationDefinitionUpdate): Promise<NotificationDefinition> {
    return sdkNotifications.updateNotificationDefinition(this.notificationsContext(), definitionId, input);
  }

  deleteNotificationDefinition(definitionId: string): Promise<{ id: string; deleted: boolean }> {
    return sdkNotifications.deleteNotificationDefinition(this.notificationsContext(), definitionId);
  }

  updateNotificationDefinitionTemplate(definitionId: string, channel: NotificationChannel, input: NotificationTemplateUpdate): Promise<NotificationTemplate> {
    return sdkNotifications.updateNotificationDefinitionTemplate(this.notificationsContext(), definitionId, channel, input);
  }

  sendNotificationTest(definitionId: string, input: { channel: Exclude<NotificationChannel, "push">; recipient: string; variables?: Record<string, unknown> }): Promise<NotificationSummary> {
    return sdkNotifications.sendNotificationTest(this.notificationsContext(), definitionId, input);
  }

  notificationTemplates(): Promise<NotificationTemplate[]> {
    return sdkNotifications.notificationTemplates(this.notificationsContext());
  }

  createNotificationTemplate(input: { channel: NotificationChannel; eventType: string; locale?: string; subject?: string; body: string; enabled?: boolean; format?: "html" | "text"; whatsappTemplate?: Record<string, unknown> | null }): Promise<NotificationTemplate> {
    return sdkNotifications.createNotificationTemplate(this.notificationsContext(), input);
  }

  updateNotificationTemplate(templateId: string, input: { channel?: NotificationChannel; subject?: string | null; body?: string; enabled?: boolean; format?: "html" | "text"; whatsappTemplate?: Record<string, unknown> | null }): Promise<{ id: string; updated: boolean }> {
    return sdkNotifications.updateNotificationTemplate(this.notificationsContext(), templateId, input);
  }

  sendNotification(input: { channel?: NotificationChannel; templateId?: string; bookingId?: string; recipient: string; scheduledAt?: string; variables?: Record<string, unknown>; idempotencyKey?: string }): Promise<{ id: string; channel: NotificationChannel; recipient: string; status: "queued"; scheduledAt: string }> {
    return sdkNotifications.sendNotification(this.notificationsContext(), input);
  }

  retryNotification(deliveryId: string): Promise<{ id: string; status: "queued" }> {
    return sdkNotifications.retryNotification(this.notificationsContext(), deliveryId);
  }

  integrations(): Promise<IntegrationSummary[]> {
    return sdkIntegrations.integrations(this.integrationsContext());
  }

  integrationCatalog(): Promise<IntegrationCatalogEntry[]> {
    return sdkIntegrations.integrationCatalog(this.integrationsContext());
  }

  createIntegration(input: { provider: string; accountLabel: string; credentials: Record<string, unknown>; environment?: PaymentProviderEnvironment; config?: { countryCode?: string; currency?: string } }): Promise<IntegrationSummary> {
    return sdkIntegrations.createIntegration(this.integrationsContext(), input);
  }

  updateIntegration(integrationId: string, input: { accountLabel?: string; credentials?: Record<string, unknown>; environment?: PaymentProviderEnvironment; config?: { countryCode?: string; currency?: string } }): Promise<IntegrationSummary> {
    return sdkIntegrations.updateIntegration(this.integrationsContext(), integrationId, input);
  }

  deleteIntegration(integrationId: string): Promise<{ id: string; deleted: true }> {
    return sdkIntegrations.deleteIntegration(this.integrationsContext(), integrationId);
  }

  googleCalendarAuthorizationUrl(integrationId: string): Promise<{ provider: "google-calendar"; integrationId: string; authUrl: string; authorizationUrl: string; stateExpiresAt: string }> {
    return sdkIntegrations.googleCalendarAuthorizationUrl(this.integrationsContext(), integrationId);
  }

  googleCalendarList(integrationId: string): Promise<{ provider: "google-calendar"; integrationId: string; calendars: Array<{ id: string; url: string; name?: string; description?: string }> }> {
    return sdkIntegrations.googleCalendarList(this.integrationsContext(), integrationId);
  }

  integrationCalendarBindings(integrationId: string): Promise<IntegrationAccountBinding[]> {
    return sdkIntegrations.integrationCalendarBindings(this.integrationsContext(), integrationId);
  }

  updateIntegrationCalendarBindings(integrationId: string, providerIds: string[]): Promise<IntegrationAccountBinding[]> {
    return sdkIntegrations.updateIntegrationCalendarBindings(this.integrationsContext(), integrationId, providerIds);
  }

  disableIntegration(integrationId: string): Promise<{ id: string; status: "disabled" }> {
    return sdkIntegrations.disableIntegration(this.integrationsContext(), integrationId);
  }

  enableIntegration(integrationId: string): Promise<{ id: string; status: "active"; lastTestStatus?: string }> {
    return sdkIntegrations.enableIntegration(this.integrationsContext(), integrationId);
  }

  credentialVersions(integrationId: string): Promise<CredentialVersion[]> {
    return sdkIntegrations.credentialVersions(this.integrationsContext(), integrationId);
  }

  revokeCredentialVersion(integrationId: string, version: number): Promise<{ id: string; integrationAccountId: string; version: number; status: "revoked" }> {
    return sdkIntegrations.revokeCredentialVersion(this.integrationsContext(), integrationId, version);
  }

  testIntegration(provider: string, integrationId?: string, options?: { live?: boolean }): Promise<{ provider: string; configured: boolean; status: string; checkedAt: string; sandbox: boolean; connectionTest?: "live" | "configuration" | "unavailable"; adapterAvailable?: boolean; supportedOperations?: string[]; account?: unknown }> {
    return sdkIntegrations.testIntegration(this.integrationsContext(), provider, integrationId, options);
  }

  ssoConnections(): Promise<SsoConnection[]> {
    return sdkIdentity.ssoConnections(this.identityContext());
  }

  createSsoConnection(input: { name: string; protocol: "oidc" | "saml"; issuer?: string; clientId?: string; clientSecret?: string; metadataUrl?: string; authorizationUrl?: string; tokenUrl?: string; jwksUrl?: string; entityId?: string; certificatePem?: string; claimMapping?: Record<string, string>; groupRoleMapping?: Partial<Record<"admin" | "manager" | "provider" | "customer", string[]>> }): Promise<SsoConnection> {
    return sdkIdentity.createSsoConnection(this.identityContext(), input);
  }

  updateSsoConnection(connectionId: string, input: { name?: string; issuer?: string | null; clientId?: string | null; clientSecret?: string; metadataUrl?: string | null; authorizationUrl?: string | null; tokenUrl?: string | null; jwksUrl?: string | null; entityId?: string | null; certificatePem?: string | null; status?: "active" | "disabled"; claimMapping?: Record<string, string>; groupRoleMapping?: Partial<Record<"admin" | "manager" | "provider" | "customer", string[]>> }): Promise<SsoConnection> {
    return sdkIdentity.updateSsoConnection(this.identityContext(), connectionId, input);
  }

  testSsoConnection(connectionId: string): Promise<{ id: string; protocol: "oidc" | "saml"; status: "ok" | "error"; checks: Record<string, string>; error: string | null }> {
    return sdkIdentity.testSsoConnection(this.identityContext(), connectionId);
  }

  ssoStartUrl(connectionId: string, redirectUri?: string): string {
    return sdkIdentity.ssoStartUrl(this.identityContext(), connectionId, redirectUri);
  }

  apiKeys(): Promise<ApiKeySummary[]> {
    return sdkIdentity.apiKeys(this.identityContext());
  }

  createApiKey(input: { name: string; scopes?: ApiKeyScope[]; expiresAt?: string | null }): Promise<ApiKeyCreated> {
    return sdkIdentity.createApiKey(this.identityContext(), input);
  }

  revokeApiKey(keyId: string): Promise<{ id: string; status: "revoked" }> {
    return sdkIdentity.revokeApiKey(this.identityContext(), keyId);
  }

  scimTokens(): Promise<ScimTokenSummary[]> {
    return sdkIdentity.scimTokens(this.identityContext());
  }

  createScimToken(input: { name: string; scopes?: string[]; expiresAt?: string | null }): Promise<ScimTokenCreated> {
    return sdkIdentity.createScimToken(this.identityContext(), input);
  }

  revokeScimToken(tokenId: string): Promise<{ id: string; status: "revoked" }> {
    return sdkIdentity.revokeScimToken(this.identityContext(), tokenId);
  }

  scimServiceProviderConfig(token: string): Promise<ScimServiceProviderConfig> {
    return sdkIdentity.scimServiceProviderConfig(this.identityContext(), token);
  }

  scimResourceTypes(token: string): Promise<ScimListResponse<Record<string, unknown>>> {
    return sdkIdentity.scimResourceTypes(this.identityContext(), token);
  }

  scimSchemas(token: string): Promise<ScimListResponse<Record<string, unknown>>> {
    return sdkIdentity.scimSchemas(this.identityContext(), token);
  }

  scimUsers(token: string, options: { startIndex?: number; count?: number; filter?: string } = {}): Promise<ScimListResponse<ScimUser>> {
    return sdkIdentity.scimUsers(this.identityContext(), token, options);
  }

  scimUser(token: string, userId: string): Promise<ScimUser> {
    return sdkIdentity.scimUser(this.identityContext(), token, userId);
  }

  createScimUser(token: string, input: ScimUserInput): Promise<ScimUser> {
    return sdkIdentity.createScimUser(this.identityContext(), token, input);
  }

  replaceScimUser(token: string, userId: string, input: ScimUserInput): Promise<ScimUser> {
    return sdkIdentity.replaceScimUser(this.identityContext(), token, userId, input);
  }

  patchScimUser(token: string, userId: string, operations: ScimPatchOperation[]): Promise<ScimUser> {
    return sdkIdentity.patchScimUser(this.identityContext(), token, userId, operations);
  }

  deleteScimUser(token: string, userId: string): Promise<void> {
    return sdkIdentity.deleteScimUser(this.identityContext(), token, userId);
  }

  scimGroups(token: string, options: { startIndex?: number; count?: number; filter?: string } = {}): Promise<ScimListResponse<ScimGroup>> {
    return sdkIdentity.scimGroups(this.identityContext(), token, options);
  }

  scimGroup(token: string, groupId: string): Promise<ScimGroup> {
    return sdkIdentity.scimGroup(this.identityContext(), token, groupId);
  }

  createScimGroup(token: string, input: ScimGroupInput): Promise<ScimGroup> {
    return sdkIdentity.createScimGroup(this.identityContext(), token, input);
  }

  replaceScimGroup(token: string, groupId: string, input: ScimGroupInput): Promise<ScimGroup> {
    return sdkIdentity.replaceScimGroup(this.identityContext(), token, groupId, input);
  }

  patchScimGroup(token: string, groupId: string, operations: ScimPatchOperation[]): Promise<ScimGroup> {
    return sdkIdentity.patchScimGroup(this.identityContext(), token, groupId, operations);
  }

  deleteScimGroup(token: string, groupId: string): Promise<void> {
    return sdkIdentity.deleteScimGroup(this.identityContext(), token, groupId);
  }

  webhookEndpoints(): Promise<WebhookEndpoint[]> {
    return sdkOperations.webhookEndpoints(this.operationsContext());
  }

  createWebhookEndpoint(input: { url: string; events?: string[] }): Promise<{ endpoint: WebhookEndpoint; secret: string }> {
    return sdkOperations.createWebhookEndpoint(this.operationsContext(), input);
  }

  updateWebhookEndpoint(endpointId: string, input: { url?: string; events?: string[]; status?: "active" | "paused" }): Promise<WebhookEndpoint> {
    return sdkOperations.updateWebhookEndpoint(this.operationsContext(), endpointId, input);
  }

  rotateWebhookEndpoint(endpointId: string): Promise<{ id: string; secret: string }> {
    return sdkOperations.rotateWebhookEndpoint(this.operationsContext(), endpointId);
  }

  deleteWebhookEndpoint(endpointId: string): Promise<{ id: string; deleted: boolean }> {
    return sdkOperations.deleteWebhookEndpoint(this.operationsContext(), endpointId);
  }

  webhookDeliveries(): Promise<WebhookDeliverySummary[]> {
    return sdkOperations.webhookDeliveries(this.operationsContext());
  }

  supportAccess(): Promise<SupportAccessSession[]> {
    return sdkOperations.supportAccess(this.operationsContext());
  }

  requestSupportAccess(input: { reason: string; scopes?: string[]; durationSeconds?: number }): Promise<Partial<SupportAccessSession> & { id: string }> {
    return sdkOperations.requestSupportAccess(this.operationsContext(), input);
  }

  approveSupportAccess(requestId: string): Promise<SupportAccessGrant> {
    return sdkOperations.approveSupportAccess(this.operationsContext(), requestId);
  }

  revokeSupportAccess(requestId: string): Promise<{ id: string; status: string }> {
    return sdkOperations.revokeSupportAccess(this.operationsContext(), requestId);
  }

  backups(options: { limit?: number; kind?: "scheduled" | "manual" | "restore_test"; status?: "queued" | "running" | "completed" | "failed"; from?: string; to?: string; beforeStartedAt?: string; beforeId?: string } = {}): Promise<BackupRun[]> {
    return sdkOperations.backups(this.operationsContext(), options);
  }

  requestBackup(kind: "manual" | "restore_test" = "manual"): Promise<Pick<BackupRun, "id" | "kind" | "status">> {
    return sdkOperations.requestBackup(this.operationsContext(), kind);
  }

  operationalHealth(): Promise<Record<string, unknown>> {
    return sdkOperations.operationalHealth(this.operationsContext());
  }

  securityEvents(options: { limit?: number; type?: string; severity?: "info" | "warning" | "critical"; from?: string; to?: string; beforeCreatedAt?: string; beforeId?: string } = {}): Promise<SecurityEvent[]> {
    return sdkOperations.securityEvents(this.operationsContext(), options);
  }

  auditIntegrity(): Promise<{ valid: boolean; checked: number; legacyUnsealed: number; invalid: Array<{ id: string; reason: string }>; headHash: string | null; checkedAt: string }> {
    return sdkOperations.auditIntegrity(this.operationsContext());
  }

  sendTrackingEvent(provider: "analytics" | "pixel", input: { eventName: string; eventId?: string; clientId?: string; timestamp?: string; sourceUrl?: string; valueCents?: number; currency?: string; userEmail?: string; customData?: Record<string, string | number | boolean>; idempotencyKey: string }): Promise<{ provider: string; accepted: boolean; eventId: string | null; providerEventId: string | null; sentAt: string }> {
    return sdkOperations.sendTrackingEvent(this.operationsContext(), provider, input);
  }

  audit(options: { limit?: number; from?: string; to?: string; beforeCreatedAt?: string; beforeId?: string } = {}): Promise<unknown[]> {
    return sdkOperations.audit(this.operationsContext(), options);
  }

  cancelBooking(bookingId: string, input: BookingCancellationInput = {}): Promise<{ id: string; status: "cancelled"; refundId?: string | null; refundEligible?: boolean }> {
    return sdkBooking.cancelBooking(this.bookingContext(), bookingId, input);
  }

  applyCoupon(bookingId: string, code: string): Promise<{ bookingId: string; couponId: string; discountCents: number; totalCents?: number }> {
    return sdkCoupons.applyCoupon(this.couponsContext(), bookingId, code);
  }

  validateCoupon(code: string, subtotalCents: number, currency = "EUR", scope?: { entityType: CouponTarget["entityType"]; entityIds: string[] }): Promise<{ code: string; discountCents: number; totalCents: number; currency: string; targets?: CouponTarget[] }> {
    return sdkCoupons.validateCoupon(this.couponsContext(), code, subtotalCents, currency, scope);
  }

  coupons(): Promise<Coupon[]> {
    return sdkCoupons.coupons(this.couponsContext());
  }

  createCoupon(input: { code: string; kind: "percentage" | "fixed"; amount: number; currency?: string; maxRedemptions?: number; validFrom?: string | null; validUntil?: string | null; status?: "active" | "inactive"; targets?: CouponTarget[] }): Promise<{ id: string; code: string; kind: string; amount: number; targets: CouponTarget[] }> {
    return sdkCoupons.createCoupon(this.couponsContext(), input);
  }

  coupon(couponId: string): Promise<CouponDetails> {
    return sdkCoupons.coupon(this.couponsContext(), couponId);
  }

  updateCoupon(couponId: string, input: { code?: string; kind?: "percentage" | "fixed"; amount?: number; currency?: string | null; maxRedemptions?: number | null; validFrom?: string | null; validUntil?: string | null; status?: "active" | "inactive"; targets?: CouponTarget[] | null }): Promise<CouponDetails> {
    return sdkCoupons.updateCoupon(this.couponsContext(), couponId, input);
  }

  updateCouponStatus(couponId: string, status: "active" | "inactive"): Promise<{ id: string; status: "active" | "inactive" }> {
    return sdkCoupons.updateCouponStatus(this.couponsContext(), couponId, status);
  }

  deleteCoupon(couponId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return sdkCoupons.deleteCoupon(this.couponsContext(), couponId);
  }

  couponRedemptions(couponId: string, filters: { status?: string; from?: string; to?: string } = {}): Promise<CouponRedemption[]> {
    return sdkCoupons.couponRedemptions(this.couponsContext(), couponId, filters);
  }

  refundPayment(input: { paymentId: string; amountCents: number; idempotencyKey: string; reason?: string; method?: "mercadopago" | "cash"; occurrenceIds?: string[]; overrideReason?: string }): Promise<RefundSummary> {
    return sdkPayment.refundPayment(this.paymentContext(), input);
  }

  events(): Promise<EventSummary[]> {
    return sdkEvents.events(this.eventsContext());
  }

  createEvent(input: { name: string; slug?: string; description?: string; timezone?: string; status?: EventSummary["status"]; categoryId?: string }): Promise<Pick<EventSummary, "id" | "name" | "slug" | "status">> {
    return sdkEvents.createEvent(this.eventsContext(), input);
  }

  event(eventId: string): Promise<EventSummary> {
    return sdkEvents.event(this.eventsContext(), eventId);
  }

  eventProviders(eventId: string): Promise<Array<{ id: string; displayName: string; status: string }>> {
    return sdkEvents.eventProviders(this.eventsContext(), eventId);
  }

  saveEventProviders(eventId: string, providerIds: string[]): Promise<Array<{ id: string; displayName: string; status: string }>> {
    return sdkEvents.saveEventProviders(this.eventsContext(), eventId, providerIds);
  }

  updateEvent(eventId: string, input: { name?: string; slug?: string; description?: string; timezone?: string; status?: EventSummary["status"] }): Promise<EventSummary> {
    return sdkEvents.updateEvent(this.eventsContext(), eventId, input);
  }

  deleteEvent(eventId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
    return sdkEvents.deleteEvent(this.eventsContext(), eventId);
  }

  cancelEvent(eventId: string): Promise<{ id: string; status: "cancelled"; cancelledAttendees: number }> {
    return sdkEvents.cancelEvent(this.eventsContext(), eventId);
  }

  eventPeriods(eventId: string): Promise<EventPeriod[]> {
    return sdkEvents.eventPeriods(this.eventsContext(), eventId);
  }

  quoteEvent(input: EventQuoteRequest): Promise<PriceQuote> {
    return sdkEvents.quoteEvent(this.eventsContext(), input);
  }

  /** Calculates and serializes the capacity for an event proposal in one operation. */
  quoteAndHoldEvent(input: EventQuoteRequest & { idempotencyKey: string }): Promise<SlotHoldResult> {
    return sdkEvents.quoteAndHoldEvent(this.eventsContext(), input);
  }

  createEventCheckout(input: EventCheckoutRequest): Promise<EventCheckout> {
    return sdkEvents.createEventCheckout(this.eventsContext(), input);
  }

  eventOrder(orderId: string): Promise<EventCheckout["order"]> {
    return sdkEvents.eventOrder(this.eventsContext(), orderId);
  }

  createEventPeriod(input: { eventId: string; startsAt: string; endsAt: string; locationId?: string; capacity: number }): Promise<EventPeriod> {
    return sdkEvents.createEventPeriod(this.eventsContext(), input);
  }

  updateEventPeriod(periodId: string, input: { startsAt?: string; endsAt?: string; locationId?: string | null; capacity?: number; status?: "open" | "closed" | "cancelled" }): Promise<EventPeriod & { cancelledAttendees?: number }> {
    return sdkEvents.updateEventPeriod(this.eventsContext(), periodId, input);
  }

  deleteEventPeriod(periodId: string): Promise<{ id: string; eventId: string; deleted: boolean }> {
    return sdkEvents.deleteEventPeriod(this.eventsContext(), periodId);
  }

  eventTickets(eventId: string): Promise<EventTicket[]> {
    return sdkEvents.eventTickets(this.eventsContext(), eventId);
  }

  createEventTicket(input: { eventId: string; name: string; description?: string; priceCents?: number; capacity?: number }): Promise<{ id: string; eventId: string; name: string }> {
    return sdkEvents.createEventTicket(this.eventsContext(), input);
  }

  updateEventTicket(ticketId: string, input: { name?: string; description?: string | null; priceCents?: number; capacity?: number; status?: "active" | "inactive" }): Promise<{ id: string; eventId: string; name: string; description?: string | null; priceCents: number; capacity: number; status: "active" | "inactive" }> {
    return sdkEvents.updateEventTicket(this.eventsContext(), ticketId, input);
  }

  deleteEventTicket(ticketId: string): Promise<{ id: string; eventId: string; status: "inactive"; deleted: boolean }> {
    return sdkEvents.deleteEventTicket(this.eventsContext(), ticketId);
  }

  createEventAttendee(input: { periodId: string; ticketId?: string; bookingId?: string; name: string; email?: string; phone?: string; idempotencyKey?: string }): Promise<EventAttendee & { qrToken?: string }> {
    return sdkEvents.createEventAttendee(this.eventsContext(), input);
  }

  eventAttendees(periodId: string): Promise<EventAttendee[]> {
    return sdkEvents.eventAttendees(this.eventsContext(), periodId);
  }

  providerEvents(): Promise<ProviderEventAssignment[]> {
    return sdkEvents.providerEvents(this.eventsContext());
  }

  checkInEventAttendee(qrToken: string): Promise<ProviderCheckInResult> {
    return sdkEvents.checkInEventAttendee(this.eventsContext(), qrToken);
  }

  cancelEventAttendee(attendeeId: string): Promise<{ id: string; status: "cancelled" }> {
    return sdkEvents.cancelEventAttendee(this.eventsContext(), attendeeId);
  }

  extras(serviceId?: string, options: { signal?: AbortSignal; includeInactive?: boolean; status?: CatalogExtra["status"] } = {}): Promise<CatalogExtra[]> {
    return sdkCatalog.extras(this.catalogContext(), serviceId, options);
  }

  createExtra(input: { serviceId: string; name: string; description?: string; priceCents?: number; durationMinutes?: number; maxQuantity?: number; status?: "active" | "inactive" }): Promise<{ id: string; serviceId: string; name: string }> {
    return sdkCatalog.createExtra(this.catalogContext(), input);
  }

  extra(extraId: string): Promise<CatalogExtra> {
    return sdkCatalog.extra(this.catalogContext(), extraId);
  }

  updateExtra(extraId: string, input: { serviceId?: string; name?: string; description?: string | null; priceCents?: number; durationMinutes?: number; maxQuantity?: number; status?: CatalogExtra["status"] }): Promise<CatalogExtra> {
    return sdkCatalog.updateExtra(this.catalogContext(), extraId, input);
  }

  deleteExtra(extraId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return sdkCatalog.deleteExtra(this.catalogContext(), extraId);
  }

  packages(options: { includeArchived?: boolean; status?: CatalogPackage["status"] } = {}): Promise<CatalogPackage[]> {
    return sdkCatalog.packages(this.catalogContext(), options);
  }

  createPackage(input: { name: string; slug?: string; description?: string; priceCents?: number; currency?: string; capacity?: number; status?: CatalogPackage["status"]; validFrom?: string; validUntil?: string }): Promise<Pick<CatalogPackage, "id" | "name" | "slug">> {
    return sdkCatalog.createPackage(this.catalogContext(), input);
  }

  getPackage(packageId: string): Promise<CatalogPackage> {
    return sdkCatalog.getPackage(this.catalogContext(), packageId);
  }

  updatePackage(packageId: string, input: { name?: string; slug?: string; description?: string | null; priceCents?: number; currency?: string; capacity?: number; status?: CatalogPackage["status"]; validFrom?: string | null; validUntil?: string | null }): Promise<CatalogPackage> {
    return sdkCatalog.updatePackage(this.catalogContext(), packageId, input);
  }

  deletePackage(packageId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
    return sdkCatalog.deletePackage(this.catalogContext(), packageId);
  }

  locations(options: { serviceId?: string; providerId?: string; organizationSlug?: string; signal?: AbortSignal } = {}): Promise<CatalogLocation[]> {
    return sdkCatalog.locations(this.catalogContext(), options);
  }

  createLocation(input: LocationInput): Promise<CatalogLocation> {
    return sdkCatalog.createLocation(this.catalogContext(), input);
  }

  updateLocation(locationId: string, input: LocationPatch): Promise<CatalogLocation> {
    return sdkCatalog.updateLocation(this.catalogContext(), locationId, input);
  }

  location(locationId: string): Promise<CatalogLocation> {
    return sdkCatalog.location(this.catalogContext(), locationId);
  }

  deleteLocation(locationId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return sdkCatalog.deleteLocation(this.catalogContext(), locationId);
  }

  providers(options: { serviceId?: string; organizationSlug?: string; signal?: AbortSignal } = {}): Promise<CatalogProvider[]> {
    return sdkCatalog.providers(this.catalogContext(), options);
  }

  resources(locationId?: string, options: { includeInactive?: boolean; status?: CatalogResource["status"] } = {}): Promise<CatalogResource[]> {
    return sdkCatalog.resources(this.catalogContext(), locationId, options);
  }

  resource(resourceId: string): Promise<CatalogResource> {
    return sdkCatalog.resource(this.catalogContext(), resourceId);
  }

  updateResource(resourceId: string, input: { locationId?: string | null; name?: string; type?: string; capacity?: number; status?: CatalogResource["status"]; metadata?: Record<string, unknown> }): Promise<CatalogResource> {
    return sdkCatalog.updateResource(this.catalogContext(), resourceId, input);
  }

  createResource(input: { locationId?: string | null; name: string; type?: string; capacity?: number; status?: CatalogResource["status"]; metadata?: Record<string, unknown> }): Promise<CatalogResource> {
    return sdkCatalog.createResource(this.catalogContext(), input);
  }

  deleteResource(resourceId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return sdkCatalog.deleteResource(this.catalogContext(), resourceId);
  }

  availabilityRules(filters: { providerId?: string; locationId?: string } = {}): Promise<AvailabilityRule[]> {
    return sdkAvailability.availabilityRules(this.availabilityContext(), filters);
  }

  createAvailabilityRule(input: { providerId?: string; serviceId?: string | null; locationId?: string; weekday: number; startTime: string; endTime: string; timezone?: string; validFrom?: string; validUntil?: string }): Promise<AvailabilityRule> {
    return sdkAvailability.createAvailabilityRule(this.availabilityContext(), input);
  }

  deleteAvailabilityRule(ruleId: string): Promise<{ id: string; deleted: boolean }> {
    return sdkAvailability.deleteAvailabilityRule(this.availabilityContext(), ruleId);
  }

  availabilityExceptions(filters: { providerId?: string; locationId?: string } = {}): Promise<AvailabilityException[]> {
    return sdkAvailability.availabilityExceptions(this.availabilityContext(), filters);
  }

  availabilityRequests(filters: { status?: ProviderAvailabilityRequest["status"]; providerId?: string } = {}): Promise<ProviderAvailabilityRequest[]> {
    return sdkAvailability.availabilityRequests(this.availabilityContext(), filters);
  }

  createAvailabilityRequest(input: {
    requestType: ProviderAvailabilityRequest["requestType"];
    action?: ProviderAvailabilityRequest["action"];
    targetId?: string;
    effectiveFrom?: string;
    serviceId?: string | null;
    locationId?: string | null;
    current?: Record<string, unknown>;
    requested: Record<string, unknown>;
    reason?: string;
    category?: string;
    urgent?: boolean;
  }): Promise<ProviderAvailabilityRequest> {
    return sdkAvailability.createAvailabilityRequest(this.availabilityContext(), input);
  }

  withdrawAvailabilityRequest(requestId: string): Promise<ProviderAvailabilityRequest> {
    return sdkAvailability.withdrawAvailabilityRequest(this.availabilityContext(), requestId);
  }

  reviewAvailabilityRequest(requestId: string, decision: "approve" | "reject", comment?: string): Promise<ProviderAvailabilityRequest> {
    return sdkAvailability.reviewAvailabilityRequest(this.availabilityContext(), requestId, decision, comment);
  }

  urgentProviderBlock(input: { startsAt: string; endsAt: string; locationId?: string | null; reason?: string; category?: string }): Promise<ProviderAvailabilityRequest> {
    return sdkAvailability.urgentProviderBlock(this.availabilityContext(), input);
  }

  providerNotifications(): Promise<ProviderPortalNotification[]> {
    return sdkAvailability.providerNotifications(this.availabilityContext());
  }

  markProviderNotificationRead(notificationId: string): Promise<{ id: string; readAt: string }> {
    return sdkAvailability.markProviderNotificationRead(this.availabilityContext(), notificationId);
  }

  providerNotificationPreferences(): Promise<NotificationPreferences> {
    return sdkAvailability.providerNotificationPreferences(this.availabilityContext());
  }

  updateProviderNotificationPreferences(input: Partial<Omit<NotificationPreferences, "userId" | "updatedAt">>): Promise<NotificationPreferences> {
    return sdkAvailability.updateProviderNotificationPreferences(this.availabilityContext(), input);
  }

  createAvailabilityException(input: { providerId?: string; serviceId?: string | null; locationId?: string; startsAt: string; endsAt: string; kind?: "blocked" | "open"; reason?: string }): Promise<AvailabilityException> {
    return sdkAvailability.createAvailabilityException(this.availabilityContext(), input);
  }

  deleteAvailabilityException(exceptionId: string): Promise<{ id: string; deleted: boolean }> {
    return sdkAvailability.deleteAvailabilityException(this.availabilityContext(), exceptionId);
  }

  waitlist(): Promise<WaitlistEntry[]> {
    return sdkCart.waitlist(this.cartContext());
  }

  offerWaitlistEntry(entryId: string, ttlSeconds?: number): Promise<SignedAction & { status: string }> {
    return sdkCart.offerWaitlistEntry(this.cartContext(), entryId, ttlSeconds);
  }

  acceptWaitlistEntry(entryId: string): Promise<{ id: string; status: "accepted" }> {
    return sdkCart.acceptWaitlistEntry(this.cartContext(), entryId);
  }

  cancelWaitlistEntry(entryId: string): Promise<{ id: string; status: "cancelled" }> {
    return sdkCart.cancelWaitlistEntry(this.cartContext(), entryId);
  }

  cart(): Promise<ShoppingCart> {
    return sdkCart.cart(this.cartContext());
  }

  addCartItem(input: { serviceId?: string; packageId?: string; eventPeriodId?: string; ticketId?: string; quantity?: number; metadata?: Record<string, unknown> }): Promise<ShoppingCart> {
    return sdkCart.addCartItem(this.cartContext(), input);
  }

  updateCartItem(itemId: string, quantity: number): Promise<ShoppingCart> {
    return sdkCart.updateCartItem(this.cartContext(), itemId, quantity);
  }

  removeCartItem(itemId: string): Promise<ShoppingCart> {
    return sdkCart.removeCartItem(this.cartContext(), itemId);
  }

  checkoutCart(input: { confirm?: boolean; paymentMethod?: "mercadopago" | "stripe" | "paypal" | "on_site"; returnUrls?: { success?: string; pending?: string; failure?: string }; idempotencyKey?: string } = {}): Promise<CartCheckout> {
    return sdkCart.checkoutCart(this.cartContext(), input);
  }

  joinWaitlist(input: { serviceId?: string; eventPeriodId?: string; email?: string; persons?: number; desiredStartAt?: string; desiredEndAt?: string }): Promise<{ id: string; status: string }> {
    return sdkCart.joinWaitlist(this.cartContext(), input);
  }

  requestPrivacyExport(): Promise<{ id: string; kind: "export"; status: string }> {
    return sdkPrivacy.requestPrivacyExport(this.privacyContext());
  }

  requestPrivacyErasure(): Promise<{ id: string; kind: "erasure"; status: string }> {
    return sdkPrivacy.requestPrivacyErasure(this.privacyContext());
  }

  privacyRequests(): Promise<PrivacyRequestSummary[]> {
    return sdkPrivacy.privacyRequests(this.privacyContext());
  }

  privacyRequestDownloadUrl(requestId: string): string {
    return sdkPrivacy.privacyRequestDownloadUrl(this.privacyContext(), requestId);
  }

  inviteUser(input: { email: string; firstName: string; lastName?: string; role: AuthUser["role"]; mfaRequired?: boolean }): Promise<UserInvitationSummary> {
    return sdkAdmin.inviteUser(this.adminContext(), input);
  }

  revokeUserInvitation(userId: string): Promise<{ userId: string; revoked: boolean }> {
    return sdkAdmin.revokeUserInvitation(this.adminContext(), userId);
  }

  acceptUserInvitation(input: { token: string; password: string; confirmPassword: string }): Promise<AcceptUserInvitationResult> {
    return sdkAdmin.acceptUserInvitation(this.adminContext(), input);
  }

  createAdminExport(input: AdminExportRequest = {}): Promise<{ id: string; format: "json" | "csv" | "zip"; scope: { entities?: string[] }; status: string }> {
    return sdkAdmin.createAdminExport(this.adminContext(), input);
  }

  adminExports(): Promise<AdminExport[]> {
    return sdkAdmin.adminExports(this.adminContext());
  }

  adminExportDownloadUrl(exportId: string): string {
    return sdkAdmin.adminExportDownloadUrl(this.adminContext(), exportId);
  }

  async createAdminImport(file: File): Promise<{ id: string; fileId: string; status: string }> {
    return createAdminImport(this.mediaContext(), file);
  }

  adminImports(): Promise<AdminImport[]> {
    return sdkAdmin.adminImports(this.adminContext());
  }

  async uploadFile(file: File): Promise<{ id: string; key: string; sizeBytes: number; contentType: string }> {
    return uploadFile(this.mediaContext(), file);
  }

  entityMedia(options: { entityType?: EntityMediaType; entityId?: string; status?: "active" | "inactive"; includeInactive?: boolean } = {}): Promise<EntityMedia[]> {
    return listEntityMedia(this.mediaContext(), options);
  }

  async createEntityMedia(input: { entityType: EntityMediaType; entityId: string; file?: File; sourceUrl?: string; position?: number; altText?: string }): Promise<EntityMedia> {
    return createEntityMedia(this.mediaContext(), input);
  }

  updateEntityMedia(mediaId: string, input: { position?: number; altText?: string | null; status?: "active" | "inactive" }): Promise<EntityMedia> {
    return updateEntityMedia(this.mediaContext(), mediaId, input);
  }

  deleteEntityMedia(mediaId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return deleteEntityMedia(this.mediaContext(), mediaId);
  }
}
